import { getSelectValue } from "../../../utils/windowHelpers";
import { hasFeature } from "tap/utils/permissions.js";

// Helper function to format form data for API
export const formatFormDataForSave = (
  formData,
  selectedWindowSequence,
  isCreateMode,
) => {
  // Transform windows array to API format
  const formattedWindows = (formData.windows || [])
    .filter((win) => win !== null && win !== undefined)
    .map((win, index) => {
      const windowStartDateValue = getSelectValue(win.windowStartDate);

      // For CREATE mode: build minimal window object
      if (isCreateMode) {
        const baseWindow = {
          windowId: getSelectValue(win.windowId),
          windowName: win.windowName ?? win.windowId?.label ?? "",
          windowOrder: index,
          windowStartDate:
            windowStartDateValue !== null && windowStartDateValue !== undefined
              ? String(windowStartDateValue)
              : null,
          windowDuration: win.windowDuration
            ? parseInt(win.windowDuration)
            : null,
          windowDurationUnit: getSelectValue(win.windowDurationUnit) || null,
        };

        // Add previousWindowPlusOneStart only for subsequent windows (index > 0)
        if (index > 0) {
          baseWindow.previousWindowPlusOneStart =
            win.previousWindowPlusOneStart ?? true;
        }

        // Add usePricingTierFrom if it exists
        const usePricingTierFromValue = getSelectValue(win.usePricingTierFrom);
        if (usePricingTierFromValue) {
          baseWindow.usePricingTierFrom = usePricingTierFromValue;
        }

        return baseWindow;
      }

      // For EDIT mode: include all original metadata + updated values
      const editWindow = {
        id: win.id,
        windowSequenceId: win.windowSequenceId,
        windowId: getSelectValue(win.windowId),
        windowName: win.windowName ?? win.windowId?.label,
        windowDescription: win.windowDescription || null,
        windowDuration: win.windowDuration
          ? parseInt(win.windowDuration)
          : null,
        windowDurationUnit: getSelectValue(win.windowDurationUnit) || null,
        createdAt: win.createdAt,
        modifiedAt: win.modifiedAt,
        createdById: win.createdById,
        modifiedById: win.modifiedById,
        windowOrder: index,
        windowStartDate:
          windowStartDateValue !== null && windowStartDateValue !== undefined
            ? windowStartDateValue // Keep as NUMBER for edit mode
            : null,
        previousWindowPlusOneStart:
          index === 0 ? null : (win.previousWindowPlusOneStart ?? true),
        usePricingTierFrom: getSelectValue(win.usePricingTierFrom) || null,
      };

      return editWindow;
    });

  // For create mode, return minimal payload
  if (isCreateMode) {
    return {
      windowSequenceName: formData.windowSequenceName || "",
      status: getSelectValue(formData.status) || "ACTIVE",
      windows: formattedWindows,
    };
  }

  // For edit mode, return full original object with updated fields
  return {
    ...selectedWindowSequence,
    windowSequenceName: formData.windowSequenceName || "",
    status: getSelectValue(formData.status) || "ACTIVE",
    windows: formattedWindows,
    errors: selectedWindowSequence.errors || {},
  };
};

// Validation function
export const validateForm = (formData, mode = "create") => {
  const errors = {};

  if (!formData.windowSequenceName?.trim()) {
    errors.windowSequenceName = "Window Sequence Name is required";
  } else {
    // Check for special characters only if name is provided
    const specialChars = /[`!@#$%^&*()_+\-={};':"\\|,.<>/?~]/;
    if (specialChars.test(formData.windowSequenceName)) {
      errors.windowSequenceName =
        "Input may not contain [`!@#$%^&*()_+-={};':\"\\|,.<>/?~]";
    }
  }

  if (!formData.status) {
    errors.status = "Status is required";
  }

  if (!formData.windows || formData.windows.length === 0) {
    errors.windows = "At least one window must be defined";
  } else {
    // Validate individual windows
    formData.windows.forEach((win, index) => {
      if (!win.windowId) {
        errors[`windows_${index}_windowId`] = "Window name is required";
      }

      if (!win.windowDuration) {
        errors[`windows_${index}_windowDuration`] = "Duration is required";
      } else if (parseInt(win.windowDuration) > 999) {
        errors[`windows_${index}_windowDuration`] =
          "Windows can not have a duration unit greater than 1000";
      }
      if (!win.windowDurationUnit) {
        errors[`windows_${index}_windowDurationUnit`] =
          "Duration unit is required";
      }

      // First window must have windowStartDate
      if (index === 0) {
        const windowStartDateValue = getSelectValue(win.windowStartDate);
        if (
          windowStartDateValue === null ||
          windowStartDateValue === undefined
        ) {
          errors[`windows_${index}_windowStartDate`] =
            "Window Start Date is required";
        }
      }

      // Subsequent windows validation
      if (index > 0) {
        const isPreviousWindowPlusOne = win.previousWindowPlusOneStart ?? true;
        const windowStartDateValue = getSelectValue(win.windowStartDate);
        const hasWindowStartDate =
          windowStartDateValue !== null && windowStartDateValue !== undefined;
        const usePricingTierFrom = getSelectValue(win.usePricingTierFrom);

        // If Previous Window +1 is OFF, Window Start Date is required
        if (!isPreviousWindowPlusOne && !hasWindowStartDate) {
          errors[`windows_${index}_windowStartDate`] = "Required";
        }

        // If Previous Window +1 is ON and no Window Start Date, then Use Pricing Tier From is required
        if (
          isPreviousWindowPlusOne &&
          !hasWindowStartDate &&
          !usePricingTierFrom
        ) {
          errors[`windows_${index}_usePricingTierFrom`] = "Required";
        }
      }

      // Check for duplicate window start dates
      const windowStartDate = getSelectValue(win.windowStartDate);
      if (windowStartDate !== null && windowStartDate !== undefined) {
        const duplicates = formData.windows.filter((w, i) => {
          const wStartDate = getSelectValue(w.windowStartDate);
          return i !== index && wStartDate === windowStartDate;
        });
        if (duplicates.length > 0) {
          errors[`windows_${index}_windowStartDate`] =
            "Multiple windows cannot have the same Window Start Date";
        }
      }

      // Validate NextWindow selection
      // In CREATE mode, skip NextWindow validation when previousWindowPlusOneStart is true
      // In EDIT mode, always validate NextWindow
      if (index > 0) {
        const isPreviousWindowPlusOne = win.previousWindowPlusOneStart ?? true;
        const usePricingTierFrom = getSelectValue(win.usePricingTierFrom);

        // For CREATE mode: only validate NextWindow if previousWindowPlusOneStart is FALSE
        // For EDIT mode: always validate NextWindow
        const shouldValidateNextWindow =
          mode === "edit" || !isPreviousWindowPlusOne;

        if (shouldValidateNextWindow && usePricingTierFrom === "NextWindow") {
          const nextWindow = formData.windows[index + 1];
          if (!nextWindow) {
            errors[`windows_${index}_usePricingTierFrom`] =
              "Please create a subsequent window in the Window Sequence before selecting this value";
          } else {
            const nextWindowStartDateValue = getSelectValue(
              nextWindow.windowStartDate,
            );
            const hasNextWindowStartDate =
              nextWindowStartDateValue !== null &&
              nextWindowStartDateValue !== undefined;
            const nextUsePricingTierFrom = getSelectValue(
              nextWindow.usePricingTierFrom,
            );

            if (!nextUsePricingTierFrom && !hasNextWindowStartDate) {
              errors[`windows_${index}_usePricingTierFrom`] =
                "Please create a subsequent window in the Window Sequence before selecting this value";
            }
          }
        }
      }
    });

    // Duplicate window names check (matches server-side validateDuplicatedWindowNames)
    // Only enforced when APAC_NEW_WINDOWS_ATTRIBUTES feature flag is disabled
    if (!hasFeature("APAC_NEW_WINDOWS_ATTRIBUTES")) {
      const windowIdCounts = {};
      formData.windows
        .filter((win) => win !== null && win !== undefined)
        .forEach((win) => {
          const wId = getSelectValue(win.windowId);
          if (wId != null) {
            windowIdCounts[wId] = (windowIdCounts[wId] || 0) + 1;
          }
        });
      const hasDuplicates = Object.values(windowIdCounts).some((c) => c > 1);
      if (hasDuplicates) {
        errors.windows =
          "Can not have multiple windows with the same window names";
      }
    }
  }

  return errors;
};

// Initial form state
export const getInitialFormState = () => ({
  windowSequenceName: "",
  status: "ACTIVE",
  windows: [
    {
      windowId: null,
      previousWindowPlusOneStart: false, // First window doesn't have this field
      windowStartDate: null,
      windowDuration: null,
      windowDurationUnit: null,
      usePricingTierFrom: null, // First window doesn't have this field
    },
  ],
});

// Transform selectedWindowSequence to form format
export const transformWindowSequenceToFormData = (windowSequence) => {
  if (!windowSequence) return getInitialFormState();

  return {
    windowSequenceName: windowSequence.windowSequenceName || "",
    status: windowSequence.status || "ACTIVE",
    windows: (windowSequence.windows || []).map((win) => ({
      // Core fields
      id: win.id,
      windowSequenceId: win.windowSequenceId,
      windowId: win.windowId,
      windowName: win.windowName,
      windowDescription: win.windowDescription,
      windowOrder: win.windowOrder,
      // Form fields
      previousWindowPlusOneStart: win.previousWindowPlusOneStart ?? false,
      windowStartDate: win.windowStartDate,
      windowDuration: win.windowDuration,
      windowDurationUnit: win.windowDurationUnit,
      usePricingTierFrom: win.usePricingTierFrom,
      // Metadata fields needed for edit payload
      createdAt: win.createdAt,
      modifiedAt: win.modifiedAt,
      createdById: win.createdById,
      modifiedById: win.modifiedById,
    })),
  };
};
